import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Modal, Tag } from 'antd';
import type { StorageProject } from '../types';

interface StorageProjectMapProps {
  projects: StorageProject[];
  width?: number;
  height?: number;
  onProjectClick?: (project: StorageProject) => void;
}

/**
 * 储能项目地图组件
 * 使用D3.js绘制世界地图，标注VRFB项目位置
 * 
 * 功能：
 * - 世界地图底图
 * - 项目标记（圆点）
 * - 标记大小映射项目容量
 * - 点击标记显示详情
 * - 悬停高亮效果
 * - 缩放和平移
 * 
 * 验证需求：8.1
 * 属性11：地图标注完整性
 */
const StorageProjectMap: React.FC<StorageProjectMapProps> = ({
  projects,
  width = 1200,
  height = 600,
  onProjectClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedProject, setSelectedProject] = useState<StorageProject | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    if (!svgRef.current || !projects.length) return;

    // 清空SVG
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    // 创建容器组（用于缩放和平移）
    const g = svg.append('g');

    // 地图投影（Mercator投影）
    const projection = d3.geoMercator()
      .scale(180)
      .translate([width / 2, height / 1.5]);

    const path = d3.geoPath().projection(projection);

    // Draw world map data
    d3.json('/data/world.json').then((data: any) => {
      if (!svgRef.current) return;

      const worldData = data;

      // Ensure we have a clean slate for map elements before redrawing
      // But we shouldn't remove everything if we want to keep the order?
      // Actually the previous code cleared everything at the start of useEffect.
      // So this is fine.

      // Draw map land
      g.selectAll('path.land')
        .data(worldData.features)
        .enter()
        .append('path')
        .attr('class', 'land')
        .attr('d', path as any)
        .attr('fill', '#e0e0e0')
        .attr('stroke', '#999')
        .attr('stroke-width', 0.5);

      // Add Country Labels
      g.selectAll('text.country-label')
        .data(worldData.features)
        .enter()
        .append('text')
        .attr('class', 'country-label')
        .attr('transform', function (d: any) {
          const centroid = path.centroid(d);
          // Verify centroid is valid (some tiny islands might return NaN)
          if (isNaN(centroid[0]) || isNaN(centroid[1])) return null;
          return `translate(${centroid[0]},${centroid[1]})`;
        })
        .attr('dy', '.35em')
        .text((d: any) => d.properties.name)
        .style('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', '#666')
        .style('opacity', function (d: any) {
          // Only show labels for regions with significant area
          const area = path.area(d);
          return area > 2000 ? 0.7 : 0; // Threshold can be adjusted
        })
        .style('pointer-events', 'none');

      drawMarkers();
    });

    const drawMarkers = () => {
      // ... existing marker logic ...
      // 计算标记大小的比例尺
      const capacityExtent = d3.extent(projects, d => d.capacity_mwh) as [number, number];
      const radiusScale = d3.scaleSqrt()
        .domain(capacityExtent)
        .range([5, 20]); // 最小5px，最大20px

      // 绘制项目标记
      const markers = g.selectAll('circle.project-marker')
        .data(projects)
        .enter()
        .append('circle')
        .attr('class', 'project-marker')
        .attr('cx', d => {
          const coords = projection([d.longitude, d.latitude]);
          return coords ? coords[0] : 0;
        })
        .attr('cy', d => {
          const coords = projection([d.longitude, d.latitude]);
          return coords ? coords[1] : 0;
        })
        .attr('r', d => radiusScale(d.capacity_mwh))
        .attr('fill', d => {
          // 根据项目状态设置颜色
          switch (d.status) {
            case 'operational':
              return '#52c41a'; // 绿色 - 运营中
            case 'under_construction':
              return '#faad14'; // 橙色 - 建设中
            case 'planned':
              return '#1890ff'; // 蓝色 - 规划中
            default:
              return '#d9d9d9'; // 灰色 - 未知
          }
        })
        .attr('fill-opacity', 0.7)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .style('cursor', 'pointer');

      // 添加悬停效果
      markers
        .on('mouseover', function (event, d) {
          d3.select(this)
            .transition()
            .duration(200)
            .attr('fill-opacity', 1)
            .attr('stroke', '#ffd700')
            .attr('stroke-width', 3);

          // 显示tooltip
          const tooltip = d3.select('body')
            .append('div')
            .attr('class', 'storage-map-tooltip')
            .style('position', 'absolute')
            .style('background', 'rgba(0, 0, 0, 0.8)')
            .style('color', '#fff')
            .style('padding', '8px 12px')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('z-index', '1000')
            .html(`
            <div><strong>${d.name}</strong></div>
            <div>${d.country} - ${d.region}</div>
            <div>容量: ${d.capacity_mwh} MWh</div>
            <div>功率: ${d.power_mw} MW</div>
          `)
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 10}px`);
        })
        .on('mousemove', function (event) {
          d3.select('.storage-map-tooltip')
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 10}px`);
        })
        .on('mouseout', function () {
          d3.select(this)
            .transition()
            .duration(200)
            .attr('fill-opacity', 0.7)
            .attr('stroke', '#fff')
            .attr('stroke-width', 1.5);

          d3.select('.storage-map-tooltip').remove();
        })
        .on('click', function (event, d) {
          event.stopPropagation();
          setSelectedProject(d);
          setModalVisible(true);
          if (onProjectClick) {
            onProjectClick(d);
          }
        });
    };

    // 添加缩放和平移功能
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 8])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // 清理函数
    return () => {
      d3.select('.storage-map-tooltip').remove();
    };
  }, [projects, width, height, onProjectClick]);

  // 格式化日期
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '未知';
    return new Date(dateStr).toLocaleDateString('zh-CN');
  };

  // 状态标签
  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      operational: { color: 'success', text: '运营中' },
      under_construction: { color: 'warning', text: '建设中' },
      planned: { color: 'processing', text: '规划中' },
    };
    const config = statusMap[status] || { color: 'default', text: '未知' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  return (
    <>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          height: 'auto',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          display: 'block'
        }}
      />

      {/* 项目详情Modal */}
      <Modal
        title="储能项目详情"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedProject && (
          <div style={{ fontSize: '14px' }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ marginBottom: '8px' }}>{selectedProject.name}</h3>
              {getStatusTag(selectedProject.status)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px' }}>
              <div style={{ color: '#666' }}>国家/地区：</div>
              <div>{selectedProject.country} - {selectedProject.region}</div>

              <div style={{ color: '#666' }}>地理位置：</div>
              <div>
                纬度 {selectedProject.latitude.toFixed(4)}°,
                经度 {selectedProject.longitude.toFixed(4)}°
              </div>

              <div style={{ color: '#666' }}>装机容量：</div>
              <div>
                <strong>{selectedProject.capacity_mwh} MWh</strong>
              </div>

              <div style={{ color: '#666' }}>功率：</div>
              <div>
                <strong>{selectedProject.power_mw} MW</strong>
              </div>

              <div style={{ color: '#666' }}>持续时间：</div>
              <div>{selectedProject.duration_hours} 小时</div>

              <div style={{ color: '#666' }}>投运时间：</div>
              <div>{formatDate(selectedProject.commissioning_date)}</div>

              <div style={{ color: '#666' }}>技术类型：</div>
              <div>{selectedProject.technology}</div>

              {selectedProject.developer && (
                <>
                  <div style={{ color: '#666' }}>开发商：</div>
                  <div>{selectedProject.developer}</div>
                </>
              )}

              <div style={{ color: '#666' }}>数据来源：</div>
              <div>{selectedProject.data_source}</div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default StorageProjectMap;
